import { useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useEventData } from "../hooks/useEventData";
import { ACCESS_LEVELS } from "../constants/accessLevels";
import styles from "./PublicEventDetail.module.scss";
import {
  Button,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  CardMedia,
} from "@mui/material";
import { format } from "date-fns";
import { cs } from "date-fns/locale";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { GlobeIcon } from "../components/Icons/Globe";
import { GalleryIcon } from "../components/Icons/Gallery";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { AuthContext } from "../context/AuthContext";
import { getInitials } from "../utils/getInitials";
import classNames from "classnames";
import { useEnumLabels } from "../hooks/useEnumLabels";

export const PublicEventDetail = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const {
    data: event,
    organiser,
    loading,
    error,
    accessLevel,
  } = useEventData(eventId!);
  const { getSkillLabelById, getInterestLabelById, getEventTypeLabelById } =
    useEnumLabels();
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const [galleryOpen, setGalleryOpen] = useState(false);

  if (loading) {
    return (
      <div className={classNames(styles.container, styles.centered)}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classNames(styles.container, styles.centered)}>
        <Alert severity="error">
          {error}
          {accessLevel === ACCESS_LEVELS.PUBLIC &&
            " Pro přístup k dalším informacím se prosím přihlaste."}
        </Alert>
      </div>
    );
  }

  if (!event) {
    return (
      <div className={classNames(styles.container, styles.centered)}>
        <Alert severity="warning">Událost nebyla nalezena</Alert>
      </div>
    );
  }

  const hasImage = Array.isArray(event?.event_image) && event.event_image[0];
  const hasAdditionalImages =
    Array.isArray(event?.event_image) &&
    event.event_image.length > 0 &&
    event.event_image.slice(1).length > 0;

  const eventTypeLabel =
    event?.event_type &&
    (typeof event.event_type === "string" ||
      typeof event.event_type === "number")
      ? getEventTypeLabelById(event.event_type)
      : null;

  const isUserAttending =
    isAuthenticated && event.my_match?.status === "approved";

  // Extract values to avoid TypeScript JSX issues
  const eventTitle: string = String(event?.event_title || event?.name || "");
  const eventAddress: string = String(event?.address || "");
  const eventDetails: string = String(
    event?.details || event?.description || "",
  );
  const eventStartDate: Date | null = event?.event_start_datetime
    ? new Date(event.event_start_datetime as string)
    : null;
  const eventSkills: number[] = (event?.skills || []) as number[];
  const eventInterests: number[] = (event?.interests || []) as number[];
  const attendeesCount: number =
    typeof event.match_count === "number" ? event.match_count : 0;
  const eventMatchPreviews = Array.isArray(event.match_previews)
    ? event.match_previews
    : [];

  return (
    <>
      <div className={styles.container}>
        {accessLevel === ACCESS_LEVELS.PUBLIC && (
          <div className={styles.publicBadge}>
            <Chip label="Veřejný detail události" size="small" />
          </div>
        )}

        <div className={styles.main}>
          {/* Sidebar with event image and organiser info */}
          <section className={styles.sidebar}>
            <div className={styles.photo}>
              {hasImage && Array.isArray(event?.event_image) ? (
                <img
                  src={event.event_image[0]?.public_link}
                  alt="event-image"
                />
              ) : (
                <CardMedia
                  className={styles.placeholderImage}
                  image={""}
                  component={"div"}
                  title="event-image"
                />
              )}
              {eventTypeLabel && (
                <div className={styles.eventType}>{eventTypeLabel}</div>
              )}
            </div>

            {organiser && (
              <div className={styles.orgWrapper}>
                <h4
                  className={styles.orgName}
                  onClick={() =>
                    navigate(`/organisation/${event?.organiser?.organiser_id}`)
                  }
                >
                  {(organiser as any).name ||
                    (organiser as any).organisation_name}
                </h4>
                <p>{(organiser as any).organisation_info}</p>
                <div className={styles.orgInfo}>
                  {(organiser as any).logo && (
                    <img
                      src={(organiser as any).logo}
                      alt="organiser-logo"
                      className={styles.orgLogo}
                    />
                  )}
                </div>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    navigate(`/organisation/${event?.organiser?.organiser_id}`)
                  }
                  fullWidth
                >
                  Zobrazit profil organizace
                </Button>
              </div>
            )}

            {hasAdditionalImages && Array.isArray(event?.event_image) && (
              <div className={styles.gallery}>
                <GalleryIcon />
                <div className={styles.images}>
                  {event.event_image.slice(1).map((img: any, idx: number) => {
                    return (
                      <img
                        key={img.id || idx}
                        src={img.public_link}
                        alt="event-image-minimized"
                        width={84}
                        height={66}
                      />
                    );
                  })}
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForwardOutlinedIcon />}
                    onClick={() => setGalleryOpen(true)}
                    className={styles.showAll}
                  >
                    Zobrazit vše
                  </Button>
                  <Lightbox
                    open={galleryOpen}
                    close={() => setGalleryOpen(false)}
                    slides={event.event_image.slice(1).map((img: any) => ({
                      src: img.public_link,
                      alt: "event-image",
                    }))}
                    plugins={[Thumbnails]}
                  />
                </div>
              </div>
            )}

            {organiser && (organiser as any).website_link && (
              <a
                href={`https://${(organiser as any).website_link}`}
                target="_blank"
                className={styles.website}
                rel="noreferrer"
              >
                <GlobeIcon />
                {(organiser as any).website_link}
              </a>
            )}
          </section>

          {/* Main content with event details */}
          <section className={styles.content}>
            <div className={styles.titleWrapper}>
              <h2 className={styles.title}>{eventTitle}</h2>
              <div className={styles.pills}>
                {isUserAttending ? (
                  <Chip
                    color="success"
                    variant="filled"
                    size="small"
                    label="Na této akci s Vámi počítají"
                    className={styles.pillAttend}
                  />
                ) : (
                  <>
                    {Array.isArray(eventInterests) &&
                      eventInterests.map((item: number, idx: number) => (
                        <Chip
                          key={idx}
                          color="primary"
                          variant="filled"
                          size="small"
                          label={getInterestLabelById(item)}
                          className={styles.pill}
                        />
                      ))}
                  </>
                )}
              </div>
            </div>

            {eventAddress && (
              <p className={styles.text}>
                <PinDropOutlinedIcon fontSize="small" />
                {eventAddress}
              </p>
            )}
            {eventStartDate && (
              <p className={styles.text}>
                <CalendarMonthOutlinedIcon fontSize="small" />
                {format(eventStartDate, "d. MMMM yyyy", { locale: cs })}
                <span>, od&nbsp; </span>
                {format(eventStartDate, "HH:mm", { locale: cs })}
              </p>
            )}

            {/* Show attendees count - visible to all */}
            {/* Show attendees count - visible to all */}
            <div className={styles.attendees}>
              Přihlášení uživatelé <span>({attendeesCount})</span>
              {typeof event.capacity === "number" && (
                <>
                  {" "}
                  / kapacita <span>({event.capacity})</span>
                </>
              )}
              <div className={styles.avatars}>
                {!attendeesCount ? (
                  <Avatar sx={{ width: 34, height: 34 }}>0</Avatar>
                ) : (
                  <>
                    {eventMatchPreviews
                      .slice(0, 5)
                      .map((preview: any, idx: number) => {
                        const initials = getInitials(preview?.first_name, "");
                        return (
                          <Avatar
                            key={idx}
                            sx={{ width: 34, height: 34 }}
                            src={preview?.profile_image}
                          >
                            {initials}
                          </Avatar>
                        );
                      })}
                    {attendeesCount > 5 && (
                      <Avatar sx={{ width: 34, height: 34 }}>
                        {`+${attendeesCount - 5}`}
                      </Avatar>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Event details - visible to all */}
            <div className={styles.info}>
              {eventDetails && (
                <div>
                  <h4>Detaily akce</h4>
                  <p className={styles.text}>{eventDetails}</p>
                </div>
              )}

              {Array.isArray(eventSkills) && eventSkills.length > 0 && (
                <div>
                  <h4>Jaké dovednosti se na akci hodí</h4>
                  <ul className={styles.list}>
                    {eventSkills.map((skill: number, idx: number) => (
                      <li key={idx} className={styles.item}>
                        {getSkillLabelById(skill)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Footer with actions - only for authenticated users */}
        {accessLevel === ACCESS_LEVELS.AUTHENTICATED && (
          <div className={styles.footer}>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              Zpět
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => navigate(`/event/${eventId}`)}
            >
              {isUserAttending ? "Spravovat účast" : "Chci se přidat"}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
